import React, { useCallback, useState } from 'react';
import { Calendar, Plus, Minus, BarChart2, Save, RefreshCw, Download, Calculator, Calendar as CalendarIcon } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import DateRangeSection from './DateRangeSection';
import CalculationTypeSection from './CalculationTypeSection';
import AdditionalOptionsSection from './AdditionalOptionsSection';
import CustomHolidaysSection from './CustomHolidaysSection';

// Импорт утилитарных функций
import { isValidDate } from '../../../../../utils/validation';
import { 
  calculateDateDifference, 
  addDays, 
  getDatesBetween, 
  getWorkingDaysBetween,
  addWorkingDays
} from '../../../../../utils/calculations/date';
import { formatDate } from '../../../../../utils/formatting';

/**
 * Компонент основной формы расчета дат
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  dateRange,
  setDateRange,
  russianHolidays,
  periodTypes,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalculationName, setSaveCalculationName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Логика для автоматического обновления типа расчета
      if (name === 'countWorkingDaysOnly' && checked) {
        newState.excludeHolidays = true;
      }

      // Сброс связанных полей, если функция отключена
      if (name === 'countSpecificDay' && !checked) {
        newState.specificDay = '1';
      }
      
      if (name === 'excludeHolidays' && !checked) {
        newState.useRussianHolidays = true;
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      startDate, endDate, calculationType, daysToAddSubtract
    } = formData;

    // Проверка корректности дат
    if (!isValidDate(startDate)) {
      newErrors.startDate = 'Укажите корректную дату начала.';
    }

    if (calculationType === 'difference' && !isValidDate(endDate)) {
      newErrors.endDate = 'Укажите корректную дату окончания.';
    }

    // Проверка для режима добавления/вычитания дней
    if (['add', 'subtract'].includes(calculationType)) {
      const numDays = parseInt(daysToAddSubtract, 10);
      if (isNaN(numDays) || numDays <= 0) {
        newErrors.daysToAddSubtract = 'Введите положительное число дней.';
      }
      if (numDays > 3650) {
        newErrors.daysToAddSubtract = 'Максимальное количество дней: 3650 (10 лет).';
      }
    }

    // Проверка порядка дат для режима разницы
    if (calculationType === 'difference' && isValidDate(startDate) && isValidDate(endDate)) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (end < start) {
        newErrors.dateOrder = 'Дата окончания должна быть не раньше даты начала.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета дат
  const calculateDates = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setDateRange([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const startDateObj = new Date(formData.startDate);
        let endDateObj = formData.calculationType === 'difference' 
          ? new Date(formData.endDate)
          : null;
        
        // Собираем все праздники
        const allHolidays = [];
        
        // Добавляем российские праздники если они включены
        if (formData.excludeHolidays && formData.useRussianHolidays) {
          allHolidays.push(...russianHolidays.map(h => h.date));
        }
        
        // Добавляем пользовательские праздники
        if (formData.excludeHolidays && formData.customHolidays.length > 0) {
          allHolidays.push(...formData.customHolidays);
        }

        // --- Выполняем расчеты в зависимости от типа ---
        let calculationResults = {};
        
        switch (formData.calculationType) {
          case 'difference': {
            // Расчет разницы между датами
            const difference = calculateDateDifference(startDateObj, endDateObj);
            
            // Расчет рабочих дней если включено
            let workingDays = 0;
            if (formData.countWorkingDaysOnly) {
              workingDays = getWorkingDaysBetween(
                startDateObj, 
                endDateObj, 
                allHolidays
              );
            }
            
            // Расчет количества определенных дней недели, если включено
            let specificDaysCount = 0;
            if (formData.countSpecificDay) {
              const specificDayNum = parseInt(formData.specificDay, 10);
              const allDates = getDatesBetween(startDateObj, endDateObj);
              specificDaysCount = allDates.filter(date => 
                date.getDay() === specificDayNum
              ).length;
            }
            
            // Количество выходных
            const weekendDays = getDatesBetween(startDateObj, endDateObj).filter(
              date => date.getDay() === 0 || date.getDay() === 6
            ).length;
            
            // Количество праздников в диапазоне
            const holidaysInRange = formData.excludeHolidays 
              ? allHolidays.filter(holiday => {
                  const holidayDate = new Date(holiday);
                  return holidayDate >= startDateObj && holidayDate <= endDateObj;
                }).length
              : 0;
            
            // Формируем результаты
            calculationResults = {
              calculationType: 'difference',
              startDate: startDateObj,
              endDate: endDateObj,
              difference,
              totalDays: formData.includeEndDate 
                ? difference.days + 1 
                : difference.days,
              workingDays,
              weekendDays,
              holidaysCount: holidaysInRange,
              specificDayCount: specificDaysCount,
              specificDayName: getDayName(parseInt(formData.specificDay, 10)),
              includeEndDate: formData.includeEndDate,
              countWorkingDaysOnly: formData.countWorkingDaysOnly,
            };
            
            // Формируем массив всех дат для визуализации календаря
            const visualDates = getDatesBetween(startDateObj, endDateObj);
            setDateRange(visualDates);
            break;
          }
          
          case 'add': {
            // Расчет даты после добавления дней
            const daysToAdd = parseInt(formData.daysToAddSubtract, 10);
            
            let resultDate;
            if (formData.countWorkingDaysOnly) {
              // Добавляем только рабочие дни
              resultDate = addWorkingDays(startDateObj, daysToAdd, allHolidays);
            } else {
              // Добавляем все дни (включая выходные и праздники)
              resultDate = addDays(startDateObj, daysToAdd);
            }
            
            // Количество выходных в промежутке
            const intermediateRange = getDatesBetween(startDateObj, resultDate);
            const weekendDays = intermediateRange.filter(
              date => date.getDay() === 0 || date.getDay() === 6
            ).length;
            
            // Количество праздников в промежутке
            const holidaysInRange = formData.excludeHolidays 
              ? allHolidays.filter(holiday => {
                  const holidayDate = new Date(holiday);
                  return holidayDate >= startDateObj && holidayDate <= resultDate;
                }).length
              : 0;
            
            // Формируем результаты
            calculationResults = {
              calculationType: 'add',
              startDate: startDateObj,
              daysAdded: daysToAdd,
              resultDate,
              weekendDays,
              holidaysCount: holidaysInRange,
              countWorkingDaysOnly: formData.countWorkingDaysOnly,
              actualDaysElapsed: Math.round((resultDate - startDateObj) / (24 * 60 * 60 * 1000))
            };
            
            // Формируем массив дат для визуализации
            const visualDates = getDatesBetween(startDateObj, resultDate);
            setDateRange(visualDates);
            break;
          }
          
          case 'subtract': {
            // Расчет даты после вычитания дней
            const daysToSubtract = parseInt(formData.daysToAddSubtract, 10);
            
            let resultDate;
            if (formData.countWorkingDaysOnly) {
              // Вычитаем только рабочие дни (добавляем отрицательное кол-во)
              resultDate = addWorkingDays(startDateObj, -daysToSubtract, allHolidays);
            } else {
              // Вычитаем все дни (включая выходные и праздники)
              resultDate = addDays(startDateObj, -daysToSubtract);
            }
            
            // Количество выходных в промежутке
            const intermediateRange = getDatesBetween(resultDate, startDateObj);
            const weekendDays = intermediateRange.filter(
              date => date.getDay() === 0 || date.getDay() === 6
            ).length;
            
            // Количество праздников в промежутке
            const holidaysInRange = formData.excludeHolidays 
              ? allHolidays.filter(holiday => {
                  const holidayDate = new Date(holiday);
                  return holidayDate >= resultDate && holidayDate <= startDateObj;
                }).length
              : 0;
            
            // Формируем результаты
            calculationResults = {
              calculationType: 'subtract',
              startDate: startDateObj,
              daysSubtracted: daysToSubtract,
              resultDate,
              weekendDays,
              holidaysCount: holidaysInRange,
              countWorkingDaysOnly: formData.countWorkingDaysOnly,
              actualDaysElapsed: Math.round((startDateObj - resultDate) / (24 * 60 * 60 * 1000))
            };
            
            // Формируем массив дат для визуализации
            const visualDates = getDatesBetween(resultDate, startDateObj);
            setDateRange(visualDates);
            break;
          }
        }

        // Обновляем результаты расчета
        setResults(calculationResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setDateRange([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, russianHolidays, setErrors, setResults, setDateRange, setIsCalculating]);

  // Функция сброса формы
  const resetForm = () => {
    setFormData({
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(new Date().setDate(new Date().getDate() + 30)).toISOString().split('T')[0],
      calculationType: 'difference',
      daysToAddSubtract: '7',
      includeEndDate: true,
      countWorkingDaysOnly: false,
      countSpecificDay: false,
      specificDay: '1',
      excludeHolidays: false,
      customHolidays: [],
      useRussianHolidays: true,
    });
    setResults(null);
    setDateRange([]);
    setErrors({});
    setSaveCalculationName('');
    setShowSaveDialog(false);
  };

  // Получение названия дня недели по его номеру
  const getDayName = (dayNumber) => {
    const days = [
      'воскресенье', 'понедельник', 'вторник', 'среда', 
      'четверг', 'пятница', 'суббота'
    ];
    return days[dayNumber] || '';
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    let csvContent = '\uFEFF'; // UTF-8 BOM для правильной кодировки в Excel
    let headers = [];
    let data = [];

    // Формируем заголовки и данные в зависимости от типа расчета
    if (results.calculationType === 'difference') {
      headers = ['Параметр', 'Значение'];
      data = [
        ['Дата начала', formatDate(results.startDate, 'short')],
        ['Дата окончания', formatDate(results.endDate, 'short')],
        ['Всего дней', results.totalDays.toString()],
        ['Дней (без учета даты окончания)', results.difference.days.toString()],
        ['Недель', results.difference.weeks.toString()],
        ['Месяцев', results.difference.months.toString()],
        ['Лет', results.difference.years.toString()],
        ['Выходных дней', results.weekendDays.toString()]
      ];

      if (results.countWorkingDaysOnly) {
        data.push(['Рабочих дней', results.workingDays.toString()]);
      }

      if (results.holidaysCount > 0) {
        data.push(['Праздничных дней', results.holidaysCount.toString()]);
      }

      if (results.specificDayCount > 0) {
        data.push([`Количество дней недели (${results.specificDayName})`, results.specificDayCount.toString()]);
      }
    } else if (results.calculationType === 'add') {
      headers = ['Параметр', 'Значение'];
      data = [
        ['Начальная дата', formatDate(results.startDate, 'short')],
        ['Добавлено дней', results.daysAdded.toString()],
        ['Итоговая дата', formatDate(results.resultDate, 'short')],
        ['Фактически прошло дней', results.actualDaysElapsed.toString()],
        ['Включая выходных дней', results.weekendDays.toString()]
      ];

      if (results.holidaysCount > 0) {
        data.push(['Включая праздничных дней', results.holidaysCount.toString()]);
      }
    } else if (results.calculationType === 'subtract') {
      headers = ['Параметр', 'Значение'];
      data = [
        ['Начальная дата', formatDate(results.startDate, 'short')],
        ['Вычтено дней', results.daysSubtracted.toString()],
        ['Итоговая дата', formatDate(results.resultDate, 'short')],
        ['Фактически прошло дней', results.actualDaysElapsed.toString()],
        ['Включая выходных дней', results.weekendDays.toString()]
      ];

      if (results.holidaysCount > 0) {
        data.push(['Включая праздничных дней', results.holidaysCount.toString()]);
      }
    }

    // Добавляем заголовки
    csvContent += headers.join(';') + '\n';

    // Добавляем данные
    data.forEach(row => {
      csvContent += row.join(';') + '\n';
    });

    // Создаем blob и скачиваем
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_дат.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalculationName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalculationName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateDates();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Calendar className="w-6 h-6 text-cyan-500" />
          Расчет дат
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Тип расчета */}
        <CalculationTypeSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диапазон дат или добавление/вычитание дней */}
        <DateRangeSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Дополнительные опции (рабочие дни и т.д.) */}
        <AdditionalOptionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Пользовательские праздники */}
        {formData.excludeHolidays && !formData.useRussianHolidays && (
          <CustomHolidaysSection 
            formData={formData} 
            setFormData={setFormData} 
            errors={errors} 
          />
        )}

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-100 dark:border-cyan-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalculationName}
                onChange={(e) => setSaveCalculationName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Выполняется расчет...' : 'Рассчитать'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать результаты (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;